import apiClient from './apiService';
import { AxiosResponse } from 'axios';
import type { AuditRecord } from '../components/common/AuditHistoryModal';

export interface Jurisdiction {
  id: number;
  entity_id: number;
  name: string;
  type?: string | null;
  enactment_law_id?: number | null;
  enactment_law_name?: string | null;
  enactment?: string | null;
  location?: string | null;
  description?: string | null;
  is_active: number;
  is_approved: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
  // Approval workflow (maker-checker)
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
}

export interface JurisdictionQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  type?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  total?: number;
}

const BASE = '/api/v1/gov/jurisdictions';

class JurisdictionsService {
  async getAll(params?: JurisdictionQueryParams): Promise<Jurisdiction[]> {
    const qp = new URLSearchParams();
    if (params?.status && params.status !== 'ALL') qp.append('status', params.status);
    if (params?.search) qp.append('search', params.search);
    if (params?.type) qp.append('type', params.type);
    const qs = qp.toString();
    const res: AxiosResponse<ApiEnvelope<Jurisdiction[]>> = await apiClient.get(`${BASE}${qs ? `?${qs}` : ''}`);
    if (res.data.success && res.data.data) return res.data.data;
    throw new Error(res.data.error || 'Failed to fetch jurisdictions');
  }

  /** Approved + active jurisdictions only — for select/combobox fields.
   *  /dropdown returns { total, eligible, items:[{id,name,code?}] } (no success/data wrapper). */
  async getDropdown(): Promise<Jurisdiction[]> {
    const res: AxiosResponse<any> = await apiClient.get(`${BASE}/dropdown`);
    const items: any[] = res.data?.items ?? [];
    return items.map((i): Jurisdiction => ({ id: i.id, name: i.name } as Jurisdiction));
  }

  async getById(id: number): Promise<Jurisdiction> {
    const res: AxiosResponse<ApiEnvelope<Jurisdiction>> = await apiClient.get(`${BASE}/${id}`);
    if (res.data.success && res.data.data) return res.data.data;
    throw new Error(res.data.error || 'Failed to fetch jurisdiction');
  }

  async create(payload: Partial<Jurisdiction>): Promise<Jurisdiction> {
    const res: AxiosResponse<ApiEnvelope<Jurisdiction>> = await apiClient.post(BASE, payload);
    if (res.data.success && res.data.data) return res.data.data;
    throw new Error(res.data.error || 'Failed to create jurisdiction');
  }

  async update(id: number, payload: Partial<Jurisdiction>): Promise<Jurisdiction> {
    const res: AxiosResponse<ApiEnvelope<Jurisdiction>> = await apiClient.put(`${BASE}/${id}`, payload);
    if (res.data.success && res.data.data) return res.data.data;
    throw new Error(res.data.error || 'Failed to update jurisdiction');
  }

  async delete(id: number): Promise<void> {
    const res: AxiosResponse<ApiEnvelope<void>> = await apiClient.delete(`${BASE}/${id}`);
    if (!res.data.success) throw new Error(res.data.error || 'Failed to delete jurisdiction');
  }

  /** Submit a draft/rejected record for approval. */
  async submit(id: number): Promise<void> {
    const res: AxiosResponse<ApiEnvelope<void>> = await apiClient.post(`${BASE}/${id}/submit`);
    if (res.data && res.data.success === false) throw new Error(res.data.error || 'Submit failed');
  }
  /** Approve a submitted record (approver scope only). */
  async approve(id: number, note?: string): Promise<void> {
    const res: AxiosResponse<ApiEnvelope<void>> = await apiClient.post(`${BASE}/${id}/approve`, { note });
    if (res.data && res.data.success === false) throw new Error(res.data.error || 'Approve failed');
  }
  /** Reject a submitted record with mandatory reason (approver scope only). */
  async reject(id: number, reason: string): Promise<void> {
    const res: AxiosResponse<ApiEnvelope<void>> = await apiClient.post(`${BASE}/${id}/reject`, { reason });
    if (res.data && res.data.success === false) throw new Error(res.data.error || 'Reject failed');
  }
  /** Reopen an approved/rejected record back to draft (approver scope only). */
  async reopen(id: number): Promise<void> {
    const res: AxiosResponse<ApiEnvelope<void>> = await apiClient.post(`${BASE}/${id}/reopen`);
    if (res.data && res.data.success === false) throw new Error(res.data.error || 'Reopen failed');
  }
  /** Fetch audit/workflow history for a jurisdiction. */
  async getHistory(id: number): Promise<AuditRecord[]> {
    const res = await apiClient.get(`${BASE}/${id}/history`);
    if (res.data?.success && res.data?.data) {
      return (res.data.data as any[]).map((r: any) => ({
        audit_id: r.id,
        action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
        changed_by_username: r.changed_by_username || 'Unknown',
        changed_on: r.changed_on,
        change_reason: r.remarks,
      }));
    }
    throw new Error(res.data?.error || 'Failed to fetch history');
  }
}

const jurisdictionsService = new JurisdictionsService();
export default jurisdictionsService;
